import React, { useState, useRef } from "react";
import API from "../../config/api";
import { useAuth } from "../../hooks/useAuth";

export default function CampaignPage() {
  const { getToken, user } = useAuth();
  const [numbers, setNumbers] = useState("");
  const [greeting, setGreeting] = useState("Hello, I'm calling from " + (user?.company_name || "our company") + ". How can I help you today?");
  const [results, setResults] = useState([]);
  const [running, setRunning] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const csvRef = useRef();

  const handleCSV = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const text = ev.target.result;
      // Extract phone numbers — grab any token matching E.164 or 10-digit Indian numbers
      const found = [];
      text.split(/[\n,;\r]+/).forEach(cell => {
        const val = cell.trim().replace(/["'\s]/g, "");
        if (/^\+\d{7,15}$/.test(val)) {
          found.push(val);
        } else if (/^[6-9]\d{9}$/.test(val)) {
          found.push("+91" + val); // auto-prefix Indian numbers
        }
      });
      if (found.length === 0) {
        alert("No valid phone numbers found in CSV. Numbers must be in E.164 format (+91XXXXXXXXXX) or 10-digit Indian format.");
        return;
      }
      setNumbers(prev => {
        const existing = prev.trim() ? prev.trim().split("\n") : [];
        const merged = [...new Set([...existing, ...found])];
        return merged.join("\n");
      });
      e.target.value = "";
    };
    reader.readAsText(file);
  };

  const runCampaign = async () => {
    const phoneList = numbers.split("\n").map(n => n.trim()).filter(n => n.startsWith("+"));
    if (phoneList.length === 0) { alert("Enter at least one phone number starting with +"); return; }
    if (!window.confirm(`Initiate ${phoneList.length} outbound call(s)?`)) return;

    setRunning(true); setResults([]); setProgress({ done: 0, total: phoneList.length });

    for (let i = 0; i < phoneList.length; i++) {
      const num = phoneList[i];
      try {
        const res = await fetch(API.triggerCall, {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${getToken()}` },
          body: JSON.stringify({ customer_number: num, initial_message: greeting }),
        });
        const data = await res.json();
        setResults(prev => [...prev, { num, status: data.status === "success" ? "✅ Initiated" : `❌ ${data.message}`, call_id: data.call_id }]);
      } catch (e) {
        setResults(prev => [...prev, { num, status: `❌ ${e.message}` }]);
      }
      setProgress({ done: i + 1, total: phoneList.length });
      // Small delay between calls
      if (i < phoneList.length - 1) await new Promise(r => setTimeout(r, 1000));
    }
    setRunning(false);
  };

  return (
    <div style={S.page}>
      <h2 style={S.title}>📞 Outbound Campaign</h2>
      <p style={S.sub}>Trigger AI voice calls to your customer list. The AI will use your knowledge base to answer questions.</p>

      <div style={S.section}>
        <label style={S.label}>Phone Numbers (one per line, E.164 format)</label>
        <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
          <button style={S.csvBtn} onClick={() => csvRef.current.click()}>
            📂 Upload CSV
          </button>
          <span style={{ color: "#8b949e", fontSize: 12, alignSelf: "center" }}>
            CSV must have phone numbers in any column (+91XXXXXXXXXX or 10-digit)
          </span>
        </div>
        <input
          ref={csvRef}
          type="file"
          accept=".csv,.txt"
          style={{ display: "none" }}
          onChange={handleCSV}
        />
        <textarea style={S.textarea} placeholder={"+919876543210\n+919876543211\n+919876543212"}
          value={numbers} onChange={e => setNumbers(e.target.value)} rows={6} />
        {numbers && (
          <p style={{ color: "#8b949e", fontSize: 12, marginTop: 4 }}>
            {numbers.split("\n").filter(n => n.trim().startsWith("+")).length} number(s) loaded
          </p>
        )}
      </div>

      <div style={S.section}>
        <label style={S.label}>Opening Greeting</label>
        <textarea style={S.textarea} value={greeting} onChange={e => setGreeting(e.target.value)} rows={3} />
        <p style={{ color: "#8b949e", fontSize: 11, marginTop: 4 }}>
          The AI will auto-detect the caller's language after the greeting.
        </p>
      </div>

      <button style={{ ...S.btn, opacity: running ? 0.6 : 1 }} onClick={runCampaign} disabled={running}>
        {running ? `Calling... (${progress.done}/${progress.total})` : "🚀 Start Campaign"}
      </button>

      {results.length > 0 && (
        <div style={{ marginTop: 24 }}>
          <h3 style={{ color: "#e6edf3", marginBottom: 12 }}>Results</h3>
          {results.map((r, i) => (
            <div key={i} style={S.resultRow}>
              <span style={{ color: "#e6edf3", fontSize: 13 }}>{r.num}</span>
              <span style={{ fontSize: 13 }}>{r.status}</span>
              {r.call_id && <span style={{ color: "#8b949e", fontSize: 11 }}>{r.call_id}</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const S = {
  page: { padding: "24px 0" },
  title: { color: "#e6edf3", marginBottom: 6 },
  sub: { color: "#8b949e", fontSize: 13, marginBottom: 24 },
  section: { marginBottom: 20 },
  label: { display: "block", color: "#8b949e", fontSize: 12, marginBottom: 6 },
  textarea: { width: "100%", padding: "10px 14px", background: "#0d1117", border: "1px solid #30363d", borderRadius: 8, color: "#fff", fontSize: 14, boxSizing: "border-box", resize: "vertical" },
  csvBtn: { padding: "8px 14px", background: "#1f6feb", border: "none", borderRadius: 8, color: "#fff", cursor: "pointer", fontSize: 13, whiteSpace: "nowrap" },
  btn: { padding: "12px 28px", background: "#238636", border: "none", borderRadius: 8, color: "#fff", fontWeight: "bold", fontSize: 15, cursor: "pointer" },
  resultRow: { display: "flex", justifyContent: "space-between", alignItems: "center", background: "#161b22", border: "1px solid #30363d", borderRadius: 8, padding: "8px 14px", marginBottom: 6 },
};
